import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react';
import { useT } from '@/i18n/useT';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

/**
 * Accessible text input. forwardRef so it drops straight into React Hook Form:
 *   <TextField label="Email" error={errors.email?.message} {...register('email')} />
 * Labels, aria-invalid and aria-describedby are wired for screen readers.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, ...rest },
  ref
) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <div className="field-input-wrap">
        <input
          id={id}
          ref={ref}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errId : hint ? hintId : undefined}
          {...rest}
        />
      </div>
      {hint && !error && (
        <div className="field-hint" id={hintId}>
          {hint}
        </div>
      )}
      {error && (
        <div className="field-error" id={errId}>
          {error}
        </div>
      )}
    </div>
  );
});

/** Password input with an accessible show/hide toggle. Also forwardRef for RHF. */
export const PasswordField = forwardRef<HTMLInputElement, TextFieldProps>(function PasswordField(
  { label, error, hint, ...rest },
  ref
) {
  const { t } = useT();
  const [shown, setShown] = useState(false);
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <div className="field-input-wrap">
        <input
          id={id}
          ref={ref}
          type={shown ? 'text' : 'password'}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errId : hint ? hintId : undefined}
          {...rest}
        />
        <button
          type="button"
          className="pw-toggle"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? t('common.hidePassword') : t('common.showPassword')}
          tabIndex={-1}
        >
          {shown ? t('common.hidePassword') : t('common.showPassword')}
        </button>
      </div>
      {hint && !error && (
        <div className="field-hint" id={hintId}>
          {hint}
        </div>
      )}
      {error && (
        <div className="field-error" id={errId}>
          {error}
        </div>
      )}
    </div>
  );
});
